import React, { ReactNode } from 'react'
import styled from '@emotion/styled'
import {
    Box,
    ButtonProps,
    CardActionArea,
    Card as MuiCard,
    CardContent as MuiCardContent,
    Typography as MuiTypography,
    Skeleton,
    Stack,
} from '@mui/material'
import { spacing } from '@mui/system'
import { grey } from '@mui/material/colors'
import QueryStatsIcon from '@mui/icons-material/QueryStats'

const Card = styled(MuiCard)`
    position: relative;
    margin-bottom: ${(props) => props.theme.spacing(6)};
    display: flex;
    height: 100%;
`

const Typography = styled(MuiTypography)(spacing)

const CardContent = styled(MuiCardContent)`
    position: relative;

    &:last-child {
        padding-bottom: ${(props) => props.theme.spacing(4)};
    }
`

const Stats = ({
    description,
    isLoading,
    isSelected,
    isSlim,
    label,
    title,
    amount,
    onClick,
}: {
    description?: string
    isLoading: boolean
    isSelected: boolean
    isSlim: boolean
    label: ReactNode
    title?: string
    amount?: ReactNode
    onClick: ButtonProps['onClick']
}) => {
    if (!title && !amount && !description) {
        return (
            <Card>
                <CardContent sx={{ height: '100%', width: '100%' }}></CardContent>
            </Card>
        )
    }

    return (
        <Card>
            <CardActionArea
                onClick={onClick}
                data-active={isSelected || undefined}
                sx={{
                    margin: isSlim ? 0 : undefined,
                    transition: 'background-color 0.5s',
                    'not([data-active])': {
                        '.description': {
                            color: grey[700],
                        },
                    },
                    '&[data-active]': {
                        backgroundColor: 'action.selected',
                        color: 'action.selectedText',
                        '&:hover': {
                            backgroundColor: 'action.selectedHover',
                        },
                    },
                }}
            >
                <CardContent
                    sx={{
                        display: 'flex',
                        flexGrow: 1,
                        paddingX: isSlim ? 2 : undefined,
                        paddingY: isSlim ? 0 : undefined,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <Box>
                            <Stack
                                direction='row'
                                spacing={2}
                                sx={{
                                    mb: 4,
                                    marginBottom: isSlim ? 2 : undefined,
                                }}
                            >
                                {label}
                                {!isSlim && (
                                    <Typography variant='h6'>{!isLoading ? `${title}` : <Skeleton />}</Typography>
                                )}
                                <div style={{ marginLeft: 'auto' }}>
                                    <QueryStatsIcon />
                                </div>
                            </Stack>
                            {isSlim && (
                                <p style={{ margin: 0 }}>
                                    {isLoading && <Skeleton width={80} />}
                                    {!isLoading && (
                                        <>
                                            <strong>{amount}</strong> {title?.toLocaleLowerCase().replace('antal ', '')}
                                        </>
                                    )}
                                </p>
                            )}
                            {!isSlim && (
                                <Typography variant='h3' mb={3} sx={{ justifyContent: 'center' }}>
                                    <Box
                                        sx={{
                                            fontWeight: 'fontWeightRegular',
                                        }}
                                    >
                                        {!isLoading ? amount : <Skeleton width={70} />}
                                    </Box>
                                </Typography>
                            )}
                        </Box>
                        {!isSlim && (
                            <Box sx={{ marginTop: 'auto' }}>
                                <Typography className='description'>{description}</Typography>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    )
}

export default Stats
